import { Button, Classes, Divider, InputGroup, Tag } from "@blueprintjs/core";
import React, { useCallback, useContext, useEffect, useState } from "react";

import { Popover2 } from "@blueprintjs/popover2";
import { DatePicker } from "@blueprintjs/datetime";

import "./DiagnosticMenu.scss";
import "components/Navbar/SecondaryNavbar.scss";
import { QueryInfo } from "types";
import { SettingsContext } from "utils/context";
import { getTimeRangeCustom, rangeKey, runKey } from "utils/utils";
import { DiagnosticToaster } from "utils/toasters";
import { useSearchParams } from "react-router-dom";

type Props = {
    id?: string;
    range?: TimeRangeOptions;
    setQueryInfo: (q: QueryInfo) => void;
};

export default function DiagnosticsMenu({ id, range, setQueryInfo }: Props) {
    const [correlationId, setCorrelationId] = useState(id ? id : "");
    const [timeRange, setTimeRange] = useState<TimeRangeOptions | undefined>(range ? range : undefined);
    const [settings] = useContext(SettingsContext);
    const [valid, setValid] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const handleIdChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            // todo: add check to match when id has no autorequest_
            const flag = /^autorequest_[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i.test(
                e.target.value
            );
            setCorrelationId(e.target.value);

            if (flag) {
                searchParams.set(runKey, btoa(e.target.value));
                setSearchParams(searchParams);
            }
            setValid(flag);
        },
        [searchParams, setSearchParams]
    );

    const handleQueryClick = useCallback(() => {
        if (!correlationId || !valid) {
            DiagnosticToaster.show({
                message: `Please Provider a Valid Request Id.`,
                intent: "warning"
            });
            return;
        }
        if (!timeRange) {
            DiagnosticToaster.show({
                message: `Please Select a Time Range.`,
                intent: "warning"
            });
            return;
        }

        const filter: FilterInput = {
            name: timeRange?.key === "custom" ? "createdOn" : "timeWindow",
            value: timeRange?.value
        };
        //todo: isAdmin probably should be renamed to showDebug Traces
        setQueryInfo({ correlationId, filter, page: [1, 200], isAdmin: settings.debugTraces });
    }, [correlationId, setQueryInfo, settings.debugTraces, timeRange, valid]);

    useEffect(() => {
        if (correlationId && timeRange && valid) {
            handleQueryClick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div className="secondary-navbar">
            <div className="center-group" style={{ maxWidth: "100%" }}>
                <InputGroup
                    asyncControl={true}
                    onChange={handleIdChange}
                    placeholder="autorequest_<guid> or <guid>"
                    value={correlationId}
                    intent={!valid ? "danger" : "primary"}
                    leftElement={<Tag minimal>Automation Request Id</Tag>}
                />
            </div>
            <Divider />
            <div className="left-group" style={{ gap: "0.5rem" }}>
                <Popover2
                    placement="bottom-end"
                    interactionKind="click"
                    content={
                        <div className="time-shortcuts">
                            <div className="bp3-datepicker bp3-elevation-1">
                                <ul tabIndex={0} className="bp3-menu bp3-daterangepicker-shortcuts">
                                    {timeWindowOptions.map((o) => (
                                        <li key={o.key}>
                                            <Button
                                                alignText="left"
                                                minimal
                                                className="bp3-menu-item bp3-popover-dismiss bp3-popover-dismiss-override"
                                                onClick={() => {
                                                    setTimeRange(o);
                                                    searchParams.set(rangeKey, btoa(JSON.stringify(o)));
                                                    setSearchParams(searchParams);
                                                }}
                                            >
                                                <div className="bp3-fill bp3-text-overflow-ellipsis">{o.text}</div>
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <DatePicker
                                highlightCurrentDay
                                className={Classes.ELEVATION_1}
                                maxDate={new Date()}
                                onChange={(e) => {
                                    const t = getTimeRangeCustom(e);
                                    setTimeRange(t);
                                    searchParams.set(rangeKey, btoa(JSON.stringify(t)));
                                    setSearchParams(searchParams);
                                }}
                            />
                        </div>
                    }
                >
                    <Button icon="filter" text={timeRange ? timeRange.text : "Time Range"} rightIcon="caret-down" />
                </Popover2>
                <Button icon="play" text="Query" intent="primary" onClick={handleQueryClick} />
            </div>
        </div>
    );
}

type FilterInput = {
    name: "timeWindow" | "createdOn";
    value: number;
};

export type TimeRangeOptions = {
    key: string;
    value: number;
    text: string;
};

export const timeWindowOptions: TimeRangeOptions[] = [
    {
        key: `30-mins`,
        text: "Last 30 minutes",
        value: 30
    },
    {
        key: `60-mins`,
        text: "Last hour",
        value: 60
    },
    {
        key: `240-mins`,
        text: "Last 4 hours",
        value: 4 * 60
    },
    {
        key: `12-hours`,
        text: "Last 12 hours",
        value: 12 * 60
    },
    {
        key: `24-hours`,
        text: "Last 24 hours",
        value: 24 * 60
    },
    {
        key: `48-hours`,
        text: "Last 48 hours",
        value: 48 * 60
    },
    {
        key: `72-hours`,
        text: "Last 3 days",
        value: 3 * 24 * 60
    },
    {
        key: `7-days`,
        text: "Last 7 days",
        value: 7 * 24 * 60
    }
];
